import { useToast } from '@vtex/admin-ui'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { useHistory, useRouteMatch } from 'react-router'

import { useIsMounted } from '../../../../../../core/hooks/useIsMounted'
import { useOrderForm } from '../../../../../../core/requests/checkout'
import { ConfirmAddressStep } from '..'
import { useCheckout } from '../../../../state'
import type { ConfirmAddressInfo } from '../../../../components/ConfirmAddress/ConfirmAddress'
import { getClientDataStateFromOrderForm } from '../../../../utils/getClientDataStateFromOrderForm'
import { CheckoutRoute } from '../../../../constants'
import { getFirstAndLastNames } from '../../../../../../utils/names'

type ConfirmAddressStepContainerProps = {
  onStepSubmit: () => void
}

export const ConfirmAddressStepContainer = ({
  onStepSubmit,
}: ConfirmAddressStepContainerProps) => {
  const {
    setClientDataState,
    checkout: { clientData },
  } = useCheckout()

  const history = useHistory()
  const { orderForm, saveClientProfileData, setShippingData } = useOrderForm()
  const [isBusy, setIsBusy] = useState(false)
  const isMountedRef = useIsMounted()
  const showToast = useToast()
  const { formatMessage } = useIntl()
  const { path } = useRouteMatch()

  const handleEditAddressClick = (info: ConfirmAddressInfo) => {
    // TODO: change after refactoring of client profile
    if (info === 'postalCode') {
      const originPath = path.includes('edit-profile') ? '-edit-profile' : ''

      history.push(
        `../confirm-postal-code?origin=${CheckoutRoute.ConfirmAdress}${originPath}`
      )

      return
    }

    const ROUTE_HASH_BY_INFO: Record<ConfirmAddressInfo, string> = {
      postalCode: '',
      neighborhood: CheckoutRoute.AddressNeighborhood,
      street: CheckoutRoute.AddressStreet,
      number: CheckoutRoute.AddressNumber,
      complement: CheckoutRoute.Complement,
    }

    history.push(
      `${ROUTE_HASH_BY_INFO[info]}?edit=true&origin=${CheckoutRoute.ConfirmAdress}`
    )
  }

  const getSelectedAddresses = (
    additionalAddressParams: Record<string, unknown> = {}
  ) => {
    const orderFormAddress = orderForm?.shippingData
      ?.address as OrderForm['shippingData']['address']

    return [
      {
        complement: clientData.address.complement,
        isDisposable: true,
        number: clientData.address.number,
        geoCoordinates: orderFormAddress.geoCoordinates,
        city: orderFormAddress.city,
        country: orderFormAddress.country,
        postalCode: orderFormAddress.postalCode,
        neighborhood: clientData.address.neighborhood ?? '',
        addressType: orderFormAddress.addressType,
        receiverName: orderFormAddress.receiverName
          ? orderFormAddress.receiverName
          : clientData.name,
        reference: orderFormAddress.reference,
        state: orderFormAddress.state,
        street: clientData.address.street ?? '',
        addressId: orderFormAddress.addressId,
        ...additionalAddressParams,
      },
    ]
  }

  const handleStepSubmit = async () => {
    setIsBusy(true)

    const [firstName, lastName] = getFirstAndLastNames(clientData.name)

    try {
      await saveClientProfileData({
        document: clientData.document,
        documentType: orderForm?.clientProfileData?.documentType ?? null,
        email: clientData.email,
        firstName,
        lastName,
        homePhone: clientData.phone,
        phone: clientData.phone,
        isCorporate: clientData.isCorporate,
        corporateName: clientData.companyInformation.companyName,
        tradeName: clientData.companyInformation.tradeName,
        corporatePhone: clientData.companyInformation.corporatePhone,
        corporateDocument: clientData.companyInformation.corporateDocument,
        stateInscription: clientData.companyInformation.stateInscription,
      })

      let newOrderForm

      const isShippingAddressResidential =
        orderForm?.shippingData.address?.addressType === 'residential'

      if (isShippingAddressResidential) {
        const selectedAddresses = getSelectedAddresses()

        newOrderForm = await setShippingData({ selectedAddresses })
      } else {
        newOrderForm = await setShippingData({
          selectedAddresses: getSelectedAddresses({
            addressType: 'residential',
            addressId: '',
          }),
        })

        newOrderForm = await setShippingData({
          selectedAddresses: getSelectedAddresses({
            addressType: 'residential',
            addressId: newOrderForm.shippingData.address?.addressId ?? '',
          }),
        })
      }

      setClientDataState(getClientDataStateFromOrderForm(newOrderForm))

      onStepSubmit()
    } catch {
      showToast({
        message: formatMessage({
          id: 'checkout.home-delivery.step.address-complement.error',
        }),
        variant: 'critical',
        dismissible: true,
      })
    } finally {
      if (isMountedRef.current) {
        setIsBusy(false)
      }
    }
  }

  return (
    <ConfirmAddressStep
      isBusy={isBusy}
      address={clientData.address}
      onStepSubmit={handleStepSubmit}
      onEditAddressInfoClick={handleEditAddressClick}
    />
  )
}
